<ada-editor-toolbar
	[editorState]="state"
	[isAnywhereEditor]="isAnywhereEditor"
	[isSaveButtonEnabled]="saveButtonEnabled | async"
>
	<ada-push-button
		data-container-selector="editor-toolbar-buttons"
		look="outline"
		text="Apply Theme"
		[adaClick]="apply | adaBindMethod: this"
		[colSpan]="0"
		[isDisabled]="!state?.value || !state?.value.objectId"
	/>
</ada-editor-toolbar>
<ada-container
	horizontalSpacing="Medium"
	id="bodyContainer"
	verticalSpacing="Medium"
	[padding]="defaultBodyPadding"
>
	<ada-card
		id="generalSettingsCard"
		[collapseEnabled]="true"
	>
		<ada-text
			data-container-selector="ada-card-header"
			[content]="'General Settings'"
		/>
		<ada-container
			data-container-selector="ada-card-body"
			horizontalSpacing="Medium"
			verticalSpacing="Medium"
			[colCount]="4"
			[padding]="defaultCardPadding"
		>
			<ada-label
				content="Theme Name"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
			/>
			<ada-input
				#NameInput
				placeholder="My Custom Theme"
				[debounce]="1000"
				[isReadOnly]="state?.value?.builtIn"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
				[showAsFullWidth]="true"
				[value]="state?.value?.name"
				(valueChange)="setName($event)"
			/>
			<ada-label
				content="Custom Logo"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
			>
				<ada-text
					data-container-selector="label-info-text"
					content="Optional: upload a custom logo to be displayed in the Admin Portal header navigation. Supported file types: {{
						acceptedFileTypes.join(', ')
					}}"
				/>
			</ada-label>
			<ada-container
				verticalSpacing="medium"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
			>
				@if (state?.value?.logo) {
					<ada-container
						class="ada-theme-logo-preview"
						padding="{{ ui.Spacing.SPACING_MEDIUM }}"
					>
						<ada-image [source]="state.value.logo" />
					</ada-container>
				}
				<ada-file-uploader
					#FileUploader
					readAs="dataUrl"
					[accept]="acceptedFileTypes"
					[isDisabled]="state?.value?.builtIn"
					[maxSize]="maxLogoSize"
					[isPreloaded]="!!state?.value?.logo"
					(fileChange)="setCustomLogo($event)"
				/>
			</ada-container>
			@if (state?.value?.logo) {
				<ada-label
					content="On Primary Logo"
					[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
				>
					<ada-text
						data-container-selector="label-info-text"
						content="Optional: upload a custom logo to be displayed on primary colored backgrounds. If not supplied, the UI will use the supplied default logo. Supported file types: {{
							acceptedFileTypes.join(', ')
						}}"
					/>
				</ada-label>
				<ada-container
					verticalSpacing="medium"
					[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
				>
					@if (state?.value?.onPrimaryLogo) {
						<ada-container
							class="ada-theme-logo-preview"
							padding="{{ ui.Spacing.SPACING_MEDIUM }}"
						>
							<ada-image [source]="state.value.onPrimaryLogo" />
						</ada-container>
					}
					<ada-file-uploader
						#FileUploader
						readAs="dataUrl"
						[accept]="acceptedFileTypes"
						[maxSize]="maxLogoSize"
						[isPreloaded]="!!state?.value?.onPrimaryLogo"
						(fileChange)="setOnPrimaryLogo($event)"
					/>
				</ada-container>
			}
			<ada-label
				content="Favicon"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
			>
				<ada-text
					data-container-selector="label-info-text"
					content="Optional: upload a custom favicon to be displayed as the browser tab icon. Supported file types: {{
						acceptedFileTypes.join(', ')
					}}"
				/>
			</ada-label>
			<ada-container
				verticalSpacing="medium"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
			>
				@if (state?.value?.favicon) {
					<ada-container
						class="ada-theme-logo-preview"
						padding="{{ ui.Spacing.SPACING_MEDIUM }}"
					>
						<ada-image [source]="state?.value.favicon" />
					</ada-container>
				}
				<ada-file-uploader
					#FileUploader
					readAs="dataUrl"
					[accept]="acceptedFaviconTypes"
					[maxSize]="maxLogoSize"
					[isPreloaded]="state?.value?.favicon"
					(fileChange)="setFavicon($event)"
				/>
			</ada-container>
		</ada-container>
	</ada-card>
	<ada-card [collapseEnabled]="true">
		<ada-text
			data-container-selector="ada-card-header"
			[content]="'Theme Colors'"
		/>
		<ada-container data-container-selector="ada-card-body">
			<ada-theme-display
				#ThemeDisplay
				[themeObject]="state?.value"
				(colorChanged)="setColor($event)"
			/>
		</ada-container>
	</ada-card>
	<ada-card
		[collapseEnabled]="true"
		[isExpanded]="false"
	>
		<ada-text
			content="Custom Styles"
			data-container-selector="ada-card-header"
		/>
		<ada-container
			data-container-selector="ada-card-body"
			colCount="{{ 4 }}"
			padding="{{ ui.Spacing.SPACING_LARGE }}"
		>
			<ada-label
				content="Custom CSS"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
			>
				<ada-container data-container-selector="label-info-text">
					<div ada-color="foreground">
						Author custom CSS to be injected into the HTML DOM when applying the Theme. <strong>Note</strong>: the
						<code>body</code> element receives a <code>data-theme</code>
						attribute set to the name of the current theme to be used for style scoping/specificity.
					</div>
				</ada-container>
			</ada-label>
			<ada-code-editor
				#CssEditor
				language="css"
				[code]="state?.value.customCss"
				[height]="'10rem'"
				[isDisabled]="state?.value.builtIn"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
				(codeChange)="state.set('customCss', $event)"
			/>
		</ada-container>
	</ada-card>
</ada-container>
